import React, { useState, useEffect, useCallback } from 'react';

import { Fab, Tooltip, Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  Add,
  DeleteForever,
  FileCopy
} from "@mui/icons-material";

import { 
  StringFeature,
  StringsFeature,
  NumberFeature,
  FloatingFeature,
  FeatureSource,
} from "@models";
import { 
  Selectable, 
  Modifier, 
  SpellModifier,
  Proficiency, 
  Ability,
  CreatureAbility,
  MinionAbility,
  ResourceFeature,
  Advantage,
  DamageMultiplier,
  ASIBaseFeature,
  BonusSpells,
  SuggestedSpells,
  Spellbook,
  LanguageFeature,
  SenseFeature,
  SpellcastingFeature,
  UpgradableNumber,
  Reroll,
  UpgradableNumberFeature,
  ConditionFeature,
  StartEquipmentFeature,
  StartEquipmentChoice,
  FeatFeature,
  UnarmedStrikesFeature,
  ReplaceAbilityScoreFeature,
  AttackUpgradeFeature,
  BonusActionAttackFeature,
  WeaponKeywordUpgradeFeature,
  CharacteristicFeature,
  SpellsFromList,
  MinimumRoll,
  SubclassFeature,
  LightAttackAddDamageMod,
  SummonUpgradeFeature,
  ActionTypeUpgrade,
  ExtraEffectsFeature,
  ExtraEffectTypeFeature,
  ExtraEffectsOfTypeFeature,
  // CreatureSpellAsAbility,
  ExtraSpellLists,
  SkillAsAbility,
  SFFeature,
  SFMergeFeature,
  TransformUpgradeFeature,
  KnowledgeFeature,
  LearnableFeature,
  WeaponMasteryFeature,
  EldritchInvocationsFeature,
  SkillAbilityOverrideFeature,
  PactBoonFeature,
  WeaponDamageUpgradeFeature,
  CharacterSheetInterface,
  CharacterFeatureSource,
  SpellDamageOptions,
  SpellbookModifier,
  AlternateConditionFeature,
  WeaponKeywordUpgradeFeature2,
  DisableActionsFeature,
  AttachedTable,
  IgnoreStealthDisadvantage,
  WeaponMasteryMasterFeature,
  SizeOptions,
  GreaterAdvantage,
  LimitSpoofFeature,
  WeaponLimitation,
  ARMOR_TYPE_OPTIONS,
  WEAPON_KEYWORD_OPTIONS,
  BonusSpellsUpgrade,
  RestAlterationFeature,
  SpellsFromListWithCosts,
  CreatureTypeFeature
} from "@dndModels";
import { 
  CHAR_FEATURE_TYPES,
  ABILITY_SCORES_EXPANDED,
  WEAPON_TYPES,
  SPELLCASTING_FOCUSES,
  CREATURE_TYPES
} from "@dndModels/Constants";
import { AbilityScoreExpandedType, CreatureType, FeatureType, WeaponType } from "@dndModels/Types";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import SelectResourceBox from "@dndComponents/select/SelectResourceBox";
import CheckBox from "@components/input/CheckBox";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import ProficiencyFeatureInput from "@dndComponents/feature/ProficiencyFeature";
import ConditionFeatureInput from "@dndComponents/feature/ConditionFeatureInput";
import SFFeatureInput from "@dndComponents/feature/SFFeature";
import SFMergeFeatureInput from "@dndComponents/feature/SFMergeFeature";
import ASIBaseFeatureInput from "@dndComponents/feature/ASIBaseFeature";
import ResourceFeatureInput from "@dndComponents/feature/ResourceFeature";
import ModifierInput from "@dndComponents/feature/ModifierInput";
import RerollInput from "@dndComponents/feature/Reroll";
import MinimumRollInput from "@dndComponents/feature/MinimumRoll";
import LanguageFeatureInput from "@dndComponents/feature/LanguageFeature";
import CharacteristicFeatureInput from "@dndComponents/feature/CharacteristicFeature";
import SpellModifierInput from "@dndComponents/feature/SpellModifier";
import AdvantageInput from "@dndComponents/feature/AdvantageInput";
import AbilityInput from "@dndComponents/feature/Ability";
import CreatureAbilityInput from "@dndComponents/feature/CreatureAbilityInput";
import MinionAbilityInput from "@dndComponents/feature/MinionAbility";
import BonusSpellsFeatureInput from "@dndComponents/feature/BonusSpellsFeatureInput";
import SpellsFromListInput from "@dndComponents/feature/SpellsFromListInput";
import SpellbookInput from "@dndComponents/feature/SpellBookInput";

import DamageMultiplierInput from "@dndComponents/feature/DamageMultiplierInput";
import SpellcastingFeatureInput from "@dndComponents/feature/SpellcastingFeature";
import SenseFeatureInput from "@dndComponents/feature/SenseFeature";
import StartEquipmentChoices from "@dndComponents/equipment/StartEquipmentChoices";
import SuggestionsInput from "@dndComponents/feature/SuggestionsInput";
import SummonUpgradeFeatureInput from "@dndComponents/feature/SummonUpgradeFeatureInput";
import ExtraEffectsFeatureInput from "@dndComponents/feature/ExtraEffectsFeatureInput";
// import CreatureSpellAsAbilityInput from './CreatureSpellAsAbilityInput';
import ExtraSpellListsInput from "@dndComponents/feature/ExtraSpellListsInput";
import SelectableInput from "@dndComponents/feature/SelectableInput";
import SkillAsAbilityInput from "@dndComponents/feature/SkillAsAbilityInput";
import SubclassFeatureInput from "@dndComponents/feature/SubclassFeatureInput";
import TransformUpgradeFeatureInput from "@dndComponents/feature/TransformUpgradeFeatureInput";
import KnowledgeFeatureInput from "@dndComponents/feature/KnowledgeFeatureInput";
import LearnableFeatureInput from "@dndComponents/feature/LearnableFeatureInput";
import AttackUpgradeFeatureInput from "@dndComponents/feature/AttackUpgradeFeatureInput";
import WeaponMasteryFeatureInput from "@dndComponents/feature/WeaponMasteryFeatureInput";
import EldritchInvocationsFeatureInput from "@dndComponents/feature/EldritchInvocationsFeatureInput";
import SkillAbilityOverrideFeatureInput from "@dndComponents/feature/SkillAbilityOverrideFeatureInput";
import PactBoonFeatureInput from "@dndComponents/feature/PactBoonFeatureInput";
import FeatFeatureInput from "@dndComponents/feature/FeatFeatureInput";
import ExtraEffectTypeFeatureInput from "@dndComponents/feature/ExtraEffectTypeFeatureInput";
import ExtraEffectsOfTypeFeatureInput from "@dndComponents/feature/ExtraEffectsOfTypeFeatureInput";
import WeaponDamageUpgradeFeatureInput from "@dndComponents/feature/WeaponDamageUpgradeFeatureInput";
import DescriptionBox from "@dndComponents/DescriptionBox";
import SpellDamageOptionsInput from "@dndComponents/feature/SpellDamageOptionsInput";
import SpellbookModifierInput from "@dndComponents/feature/SpellBookModifierInput";
import AlternateConditionFeatureInput from "@dndComponents/feature/AlternateConditionFeatureInput";
import DisableActionsFeatureInput from "@dndComponents/feature/DisableActionsFeatureInput";
import WeaponMasteryMasterFeatureInput from "@dndComponents/feature/WeaponMasteryMasterFeatureInput";
import SizeOptionsInput from "@dndComponents/feature/SizeOptionsInput";
import GreaterAdvantageInput from "@dndComponents/feature/GreaterAdvantage";
import WeaponKeywordUpgradeFeatureInput from "@dndComponents/feature/WeaponKeywordUpgradeFeatureInput";
import LimitSpoofFeatureInput from "@dndComponents/feature/LimitSpoofFeatureInput";
import WeaponLimitInput from "@dndComponents/limitations/WeaponLimitInput";
import BonusSpellsUpgradeFeatureInput from "@dndComponents/feature/BonusSpellsUpgradeFeatureInput";
import RestAlterationFeatureInput from "@dndComponents/feature/RestAlterationFeatureInput";
import LoadingBox from "@components/input/LoadingBox";
import SpellsFromListWithCostsInput from "@dndComponents/feature/SpellsFromListWithCostsInput";
import UnarmedStrikesFeatureInput from "@dndComponents/feature/UnarmedStrikesFeatureInput";
import CopyButton from '@components/input/buttons/icon_buttons/CopyButton';

interface Props {
  floating_feature: FloatingFeature | null;
  feature_source?: FeatureSource;
  cfs?: CharacterSheetInterface | CharacterFeatureSource;
  height?: number;
  slot_level?: number | undefined;
  onAdd?: ((floating_feature: FloatingFeature) => void) | null;
  onAdd2?: ((floating_feature: FloatingFeature) => void) | null;
  unsavedChange?: ((unsaved_changes: boolean) => void) | null;
  allowed_feature_types?: FeatureType[];
  attached_table?: AttachedTable;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

const get_floating_feature = (ff: FloatingFeature | null) => {
  const new_ff = ff ? api.new_feature_of_type(ff.feature_type) : new FloatingFeature();
  if (ff) {
    new_ff.copy(ff);
  } else if (api.user && !api.user.admin) {
    new_ff.owner_id = api.user._id;
  }
  return new_ff;
}

export default function FloatingFeatureInput({ 
  floating_feature,
  feature_source,
  height = window.innerHeight - 220,
  allowed_feature_types = CHAR_FEATURE_TYPES,
  onAdd = null,
  onAdd2 = null,
  slot_level = undefined,
  unsavedChange = null,
  cfs,
  attached_table
}: Props) {
  const [unsaved_changes, setUnsavedChanges] = useState(false);
  const [my_ff, setFF] = useState(new FloatingFeature());
  // const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [change_time, setChangeTime] = useState(0);
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);

  const enabled = (my_ff.owner_id === "" && api.user && api.user.admin) || (my_ff.owner_id === api.user_id); 
  const disabled = !enabled;

  const load = useCallback(async () => {
    if (floating_feature) {
      const ff = get_floating_feature(floating_feature);
      if (!ff.loaded) {
        setLoading(true);
        await api.loadObject(ff);
        setLoading(false);
      }
      setFF(ff);
    }
  }, [floating_feature]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, setFetched, load]);
  
  // useEffect(() => {
  //   if (reloading) {
  //     setReloading(false);
  //   }
  // }, [reloading, setReloading]);

  const submit = async () => {
    if (api.user) {
      const new_ff = my_ff;
      setProcessing(true);
      if (new_ff._id === "") {
        new_ff.owner_id = api.user_id;
      }
      new_ff.owner_name = api.user.use_name;
      const res = await api.upsertObject(new_ff.data_type, new_ff);
      if (floating_feature && floating_feature._id === new_ff._id) {
        floating_feature.copy(new_ff);
      } else if (res) {
        new_ff._id = res.body.id;
        new_ff.loaded = true;
        api.add_floating_feature(new_ff);
        if (onAdd) {
          onAdd(new_ff);
        }
      }
      setProcessing(false);
      setUnsavedChanges(false);
      if (unsavedChange) {
        unsavedChange(false);
      }
    }
  }

  const makeUpdate = (ff: FloatingFeature) => {
    const new_ff = my_ff;
    // debugger;
    new_ff.copy(ff, true);
    setFF(new_ff);
    setUnsavedChanges(true);
    setChangeTime(common_util.get_time_number());
    if (unsavedChange) {
      unsavedChange(true);
    }
  }

  const renderDetails = () => {
    const disabled = api.user === null || !api.online || 
      (!api.user.admin && 
        my_ff.owner_id !== api.user._id);
    let feature_details = <span></span>;
    if (my_ff !== null) {
      switch(my_ff.feature_type) {
        case "Language":
          if (my_ff instanceof LanguageFeature) {
            const language = my_ff as LanguageFeature;
            feature_details = 
              <LanguageFeatureInput
                obj={language.clone()}
                change_time={change_time}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Pact Boon":
          if (my_ff instanceof PactBoonFeature) {
            feature_details = 
              <PactBoonFeatureInput 
                obj={my_ff} 
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Proficiency Override":
          if (my_ff instanceof StringFeature) {
            feature_details = 
              <span>An item with this feature will count as proficient to the character.</span>;
          }
        break;
        case "Ignore Heavy Rule":
          if (my_ff instanceof StringFeature) {
            feature_details = 
              <span>A Heavy Weapon with this feature will ignore Disadvantage due to either the old or new Heavy Rule.</span>;
          }
        break;
        case "Jack of All Trades":
          if (my_ff instanceof StringFeature) {
            feature_details = 
              <span>The player will have half proficiency on all of their non-proficient ability checks.</span>;
          }
        break;
        case "Eldritch Invocations":
          if (my_ff instanceof EldritchInvocationsFeature) {
            feature_details = 
              <EldritchInvocationsFeatureInput 
                obj={my_ff} 
                disabled={disabled}
                onChange={makeUpdate}
                attached_table={attached_table}
              />;
          }
        break;
        case "Creature Type":
          if (my_ff instanceof CreatureTypeFeature) {
            feature_details = (
              <SelectStringBox
                name="Creature Type"
                options={CREATURE_TYPES}
                value={my_ff.creature_type}
                disabled={disabled}
                onChange={(changed: CreatureType) => {
                  const new_ff = my_ff as CreatureTypeFeature;
                  new_ff.creature_type = changed;
                  makeUpdate(new_ff);
                }}
              />
            );
          }
        break;
        case "Fighting Style":
          if (my_ff instanceof StringsFeature) {
            const choices = my_ff as StringsFeature;
            feature_details = 
              <SelectObjectBox
                name="Fighting Style Options"
                data_type="fighting_style"
                values={choices.strings}
                multiple
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Reroll":
          if (my_ff instanceof Reroll) {
            const reroll = my_ff as Reroll;
            feature_details = 
              <RerollInput
                obj={reroll.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Modifier":
          if (my_ff instanceof Modifier) {
            const modifier = my_ff as Modifier;
            feature_details = 
              <ModifierInput
                obj={modifier.clone()}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Transform Upgrade":
          if (my_ff instanceof TransformUpgradeFeature) {
            const transform_feature = my_ff as TransformUpgradeFeature;
            feature_details = 
              <TransformUpgradeFeatureInput
                obj={transform_feature.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Knowledge":
          if (my_ff instanceof KnowledgeFeature) {
            const knowledge_feature = my_ff as KnowledgeFeature;
            feature_details = 
              <KnowledgeFeatureInput
                obj={knowledge_feature.clone()}
                disabled={disabled}
                change_time={change_time}
                height={height}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Learnable":
          if (my_ff instanceof LearnableFeature) {
            const learnable_feature = my_ff as LearnableFeature;
            feature_details = 
              <LearnableFeatureInput
                obj={learnable_feature.clone()}
                disabled={disabled}
                change_time={change_time}
                height={height}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Minimum Roll":
          if (my_ff instanceof MinimumRoll) {
            const min_roll = my_ff as MinimumRoll;
            feature_details = 
              <MinimumRollInput
                obj={min_roll.clone()}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Unarmed Strikes":
          if (my_ff instanceof UnarmedStrikesFeature) {
            const usf = my_ff as UnarmedStrikesFeature;
            feature_details = 
              <UnarmedStrikesFeatureInput
                obj={usf}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Replace Ability Score for Attacks":
          if (my_ff instanceof ReplaceAbilityScoreFeature) {
            const rasf = my_ff as ReplaceAbilityScoreFeature;
            feature_details = 
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 6
                  }}>
                  <SelectStringBox 
                    name="Attack Modifier" 
                    options={ABILITY_SCORES_EXPANDED}
                    value={rasf.use_ability_score} 
                    disabled={disabled}
                    onChange={(changed: AbilityScoreExpandedType) => {
                      const new_ff = my_ff as ReplaceAbilityScoreFeature;
                      new_ff.use_ability_score = changed;
                      makeUpdate(new_ff);
                    }} 
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6
                  }}>
                  <CheckBox 
                    name="Magical Weapons Only" 
                    value={rasf.magical_weapons_only} 
                    disabled={disabled}
                    onChange={(changed: boolean) => {
                      const new_ff = my_ff as ReplaceAbilityScoreFeature;
                      new_ff.magical_weapons_only = changed;
                      makeUpdate(new_ff);
                    }} 
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <SelectStringBox 
                    name="Weapon Keywords" 
                    options={WEAPON_KEYWORD_OPTIONS}
                    values={rasf.weapon_keyword_ids} 
                    multiple
                    disabled={disabled}
                    onChange={(changed: string[]) => {
                      const new_ff = my_ff as ReplaceAbilityScoreFeature;
                      new_ff.weapon_keyword_ids = changed;
                      makeUpdate(new_ff);
                    }} 
                  />
                </Grid>
                { rasf.use_ability_score === "Spellcasting" &&
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <SelectObjectBox 
                      name="For Class" 
                      data_type="game_class"
                      value={rasf.game_class_id} 
                      disabled={disabled}
                      onChange={(changed: string) => {
                        const new_ff = my_ff as ReplaceAbilityScoreFeature;
                        new_ff.game_class_id = changed;
                        makeUpdate(new_ff);
                      }} 
                    />
                  </Grid>
                }
              </Grid>;
          }
        break;
        case "Attack Upgrade":
          if (my_ff instanceof AttackUpgradeFeature) {
            const auf = my_ff as AttackUpgradeFeature;
            feature_details = 
              <AttackUpgradeFeatureInput
                obj={auf}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Weapon Mastery":
          if (my_ff instanceof WeaponMasteryFeature) {
            const wmf = my_ff as WeaponMasteryFeature;
            feature_details = 
              <WeaponMasteryFeatureInput
                obj={wmf}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Weapon Mastery Master":
          if (my_ff instanceof WeaponMasteryMasterFeature) {
            const wmf = my_ff as WeaponMasteryMasterFeature;
            feature_details = 
              <WeaponMasteryMasterFeatureInput
                obj={wmf}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Extra Effects":
          if (my_ff instanceof ExtraEffectsFeature) {
            const eef = my_ff as ExtraEffectsFeature;
            feature_details = 
              <ExtraEffectsFeatureInput
                obj={eef.clone()}
                feature_source={feature_source}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Extra Effect Type":
          if (my_ff instanceof ExtraEffectTypeFeature) {
            const edrf = my_ff as ExtraEffectTypeFeature;
            feature_details = 
              <ExtraEffectTypeFeatureInput
                obj={edrf.clone()}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Extra Effects of Type":
          if (my_ff instanceof ExtraEffectsOfTypeFeature) {
            const edrf = my_ff as ExtraEffectsOfTypeFeature;
            feature_details = 
              <ExtraEffectsOfTypeFeatureInput
                obj={edrf.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Disable Actions":
          if (my_ff instanceof DisableActionsFeature) {
            const eef = my_ff as DisableActionsFeature;
            feature_details = 
              <DisableActionsFeatureInput
                obj={eef.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Bonus Action Attack":
          if (my_ff instanceof BonusActionAttackFeature) {
            const baaf = my_ff as BonusActionAttackFeature;
            feature_details = 
              <Grid container spacing={0} direction="column">
                <Grid>
                  <WeaponLimitInput
                    obj={baaf.weapon_limit}
                    disabled={disabled}
                    onChange={(changed: WeaponLimitation) => {
                      const new_ff = my_ff as BonusActionAttackFeature;
                      new_ff.weapon_limit = changed.clone();
                      makeUpdate(new_ff);
                    }}
                  />
                </Grid>
              </Grid>;
          }
        break;
        case "Light Attack Add Damage Mod":
          if (my_ff instanceof LightAttackAddDamageMod) {
            const laadm = my_ff as LightAttackAddDamageMod;
            feature_details = 
            <Grid container spacing={0} direction="column">
              <Grid>
                <WeaponLimitInput
                  obj={laadm.weapon_limit}
                  disabled={disabled}
                  onChange={(changed: WeaponLimitation) => {
                    const new_ff = my_ff as LightAttackAddDamageMod;
                    new_ff.weapon_limit = changed.clone();
                    makeUpdate(new_ff);
                  }}
                />
              </Grid>
            </Grid>;
          }
        break;
        case "Weapon Keyword Upgrade":
          if (my_ff instanceof WeaponKeywordUpgradeFeature) {
            feature_details = (
              <WeaponKeywordUpgradeFeatureInput
                obj={my_ff}
                disabled={disabled}
                onChange={makeUpdate}
              />
            );
          }
        break;
        case "Weapon Keyword Upgrade 2":
          if (my_ff instanceof WeaponKeywordUpgradeFeature2) {
            const wkuf = my_ff as WeaponKeywordUpgradeFeature2;
            const thrown = WEAPON_KEYWORD_OPTIONS.find(o => o.text === "Thrown");
            const thrown_id = thrown?.id??"";
            feature_details = 
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: wkuf.weapon_type === "Selectable" ? 6 : 12
                  }}>
                  <SelectStringBox
                    name="Weapon Type" 
                    value={ wkuf.weapon_type } 
                    options={WEAPON_TYPES}
                    extra_options={["Selectable"]}
                    disabled={disabled}
                    onChange={(changed: WeaponType | "Selectable") => {
                      const new_ff = my_ff as WeaponKeywordUpgradeFeature2;
                      new_ff.weapon_type = changed;
                      makeUpdate(new_ff);
                    }} 
                  />
                </Grid>
                { wkuf.weapon_type === "Selectable" && 
                  <Grid
                    size={{
                      xs: 6
                    }}>
                    <StringBox
                      name="Weapon Type Selectable Key"
                      value={wkuf.selectable_key}
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_ff = my_ff as WeaponKeywordUpgradeFeature2;
                        new_ff.selectable_key = changed;
                        makeUpdate(new_ff);
                      }}
                    />
                  </Grid>
                }
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <SelectStringBox 
                    name="Weapon Keywords to Add to Selected" 
                    options={WEAPON_KEYWORD_OPTIONS}
                    values={wkuf.added_wk_ids} 
                    multiple
                    disabled={disabled}
                    onChange={(changed: string[]) => {
                      const new_ff = my_ff as WeaponKeywordUpgradeFeature2;
                      new_ff.added_wk_ids = changed;
                      makeUpdate(new_ff);
                    }} 
                  />
                </Grid>
                { thrown_id !== "" && wkuf.added_wk_ids.includes(thrown_id) &&
                  <>
                    <Grid
                      size={{
                        xs: 6
                      }}>
                      <StringBox 
                        name="Range (in feet)"
                        value={`${wkuf.range}`}
                        type="number"
                        disabled={disabled}
                        onBlur={(changed: string) => {
                          const new_ff = my_ff as WeaponKeywordUpgradeFeature2;
                          new_ff.range = +changed;
                          makeUpdate(new_ff);
                        }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 6
                      }}>
                      <StringBox 
                        name="Long Range (in feet)"
                        value={`${wkuf.range2}`}
                        type="number"
                        disabled={disabled}
                        onBlur={(changed: string) => {
                          const new_ff = my_ff as WeaponKeywordUpgradeFeature2;
                          new_ff.range2 = +changed;
                          makeUpdate(new_ff);
                        }}
                      />
                    </Grid>
                  </>
                }
              </Grid>;
          }
        break;
        case "Weapon Damage Upgrade":
          if (my_ff instanceof WeaponDamageUpgradeFeature) {
            const wduf = my_ff as WeaponDamageUpgradeFeature;
            feature_details = 
              <WeaponDamageUpgradeFeatureInput
                obj={wduf}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Resource Refresh Rule Change":
          if (my_ff instanceof StringsFeature) {
            const ssf = my_ff as StringsFeature;
            console.log(ssf);
            feature_details = 
              <Grid container spacing={0} direction="column">
                <Grid>
                  <SelectResourceBox 
                    name="Resource Type"
                    disabled={disabled}
                    value={ssf.strings[0]}
                    onChange={(value: string) => {
                      const new_ff = my_ff as StringsFeature;
                      new_ff.strings[0] = value;
                      makeUpdate(new_ff);
                    }}
                  />
                </Grid>
                <Grid>
                  <SelectStringBox 
                    name="New Refresh Rule" 
                    options={["Long Rest","Short Rest"]}
                    value={ssf.strings[1]} 
                    disabled={disabled}
                    onChange={(value: string) => {
                      const new_ff = my_ff as StringsFeature;
                      new_ff.strings[1] = value;
                      makeUpdate(new_ff);
                    }} 
                  />
                </Grid>
              </Grid>;
          }
        break;
        case "Subclass":
          if (my_ff instanceof SubclassFeature) {
            const subclass_feature = my_ff as SubclassFeature;
            feature_details =
              <SubclassFeatureInput
                obj={subclass_feature}
                disabled={disabled}
                onChange={makeUpdate}
              />
          }
        break;
        case "Subrace":
          feature_details =
            <div>Users will be able to choose a subrace</div>;
        break;
        case "Start Equipment":
          if (my_ff instanceof StartEquipmentFeature) {
            const sf = my_ff as StartEquipmentFeature;
            feature_details = 
              <StartEquipmentChoices
                choices={sf.start_equipment}
                disabled={ disabled }
                onChange={(choices: StartEquipmentChoice[]) => {
                  const new_ff = (my_ff as StartEquipmentFeature).clone();
                  new_ff.start_equipment = choices.map(o => o.clone());
                  makeUpdate(new_ff);
                }}
              />;
          }
        break;
        case "Characteristic":
          if (my_ff instanceof CharacteristicFeature) {
            const sf = my_ff as CharacteristicFeature;
            feature_details = 
              <CharacteristicFeatureInput
                obj={sf}
                disabled={ disabled }
                onChange={makeUpdate}
              />;
          }
        break;
        case "Spell Modifier":
          if (my_ff instanceof SpellModifier) {
            const modifier = my_ff as SpellModifier;
            feature_details = 
              <SpellModifierInput
                obj={modifier.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Spell Damage Options":
          if (my_ff instanceof SpellDamageOptions) {
            const sdo = my_ff as SpellDamageOptions;
            feature_details = 
              <SpellDamageOptionsInput
                obj={sdo.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Size Options":
          if (my_ff instanceof SizeOptions) {
            const so = my_ff as SizeOptions;
            feature_details = 
              <SizeOptionsInput
                obj={so.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Summon Upgrade":
          if (my_ff instanceof SummonUpgradeFeature) {
            const suf = my_ff as SummonUpgradeFeature;
            feature_details = 
              <SummonUpgradeFeatureInput
                obj={suf.clone()}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Condition":
          if (my_ff instanceof ConditionFeature) {
            const sff = my_ff as ConditionFeature;
            feature_details = 
              <ConditionFeatureInput
                sff={sff.clone()}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Alternate Condition":
          if (my_ff instanceof AlternateConditionFeature) {
            const sff = my_ff;
            feature_details = 
              <AlternateConditionFeatureInput
                sff={sff.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Allowed Conditions":
          if (my_ff instanceof StringsFeature) {
            const sf = my_ff as StringsFeature;
            feature_details = 
              <SelectObjectBox
                name="Allowed Conditions"
                data_type="condition"
                filter={{ always_show: false }}
                multiple
                disabled={disabled}
                values={sf.strings}
                onChange={(changed: string[]) => {
                  const new_ff = my_ff as StringsFeature;
                  new_ff.strings = changed;
                  makeUpdate(new_ff);
                }}
              />;
          }
        break;
        case "Limit Spoof":
          if (my_ff instanceof LimitSpoofFeature) {
            const lof = my_ff as LimitSpoofFeature;
            feature_details = 
              <LimitSpoofFeatureInput
                obj={lof.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Rest Alteration":
          if (my_ff instanceof RestAlterationFeature) {
            feature_details = 
              <RestAlterationFeatureInput
                obj={my_ff.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Special Feature":
        case "Specific Special Feature":
        case "Special Feature Choices":
          if (my_ff instanceof SFFeature) {
            const sff = my_ff as SFFeature;
            feature_details = 
              <SFFeatureInput
                obj={sff.clone()}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Special Feature List Merge":
          if (my_ff instanceof SFMergeFeature) {
            const sff = my_ff as SFMergeFeature;
            feature_details = 
              <SFMergeFeatureInput
                sff={sff.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Sense":
          if (my_ff instanceof SenseFeature) {
            const sense_feature = my_ff as SenseFeature;
            feature_details = 
              <SenseFeatureInput
                obj={sense_feature.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Proficiencies":
        case "Expertise":
          if (my_ff instanceof Proficiency) {
            const proficiency = my_ff as Proficiency;
            feature_details = 
              <ProficiencyFeatureInput
                proficiency={proficiency.clone()}
                change_time={change_time}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Skill Ability Override":
          if (my_ff instanceof SkillAbilityOverrideFeature) {
            const saof = my_ff as SkillAbilityOverrideFeature;
            feature_details = 
              <SkillAbilityOverrideFeatureInput
                obj={saof.clone()}
                change_time={change_time}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Ability":
          if (my_ff instanceof Ability) {
            const ability = my_ff as Ability;
            feature_details = 
              <AbilityInput 
                obj={ability.clone()}
                feature_source={feature_source}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Skill as Ability":
          if (my_ff instanceof SkillAsAbility) {
            const ability = my_ff as SkillAsAbility;
            feature_details = 
              <SkillAsAbilityInput 
                obj={ability.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Creature Ability":
          if (my_ff instanceof CreatureAbility) {
            const ability = my_ff as CreatureAbility;
            feature_details =
              <CreatureAbilityInput 
                obj={ability.clone()}
                disabled={disabled}
                onChange={makeUpdate}
                attached_table={attached_table}
              />;
          }
        break;
        case "Minion Ability":
          if (my_ff instanceof MinionAbility) {
            const ability = my_ff as MinionAbility;
            feature_details =
              <MinionAbilityInput 
                obj={ability.clone()}
                slot_level={slot_level}
                disabled={disabled}
                onChange={makeUpdate}
                attached_table={attached_table}
              />;
          }
        break;
        case "Extra Attacks":
          if (my_ff instanceof NumberFeature) {
            const attacks = my_ff as NumberFeature;
            feature_details =
              <StringBox 
                name="Extra Attacks"
                value={`${attacks.num}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_ff = my_ff as NumberFeature;
                  new_ff.num = +changed;
                  makeUpdate(new_ff);
                }}
              />;
          }
        break;
        case "Minion Extra Attacks":
          if (my_ff instanceof UpgradableNumberFeature) {
            const attacks = my_ff as UpgradableNumberFeature;
            feature_details =
              <UpgradableNumberBox 
                name="Minion Extra Attacks"
                slot_level={slot_level}
                value={attacks.num} 
                disabled={disabled}
                attached_table={attached_table}
                onChange={(changed: UpgradableNumber) => {
                  const new_ff = my_ff as UpgradableNumberFeature;
                  new_ff.num = changed;
                  makeUpdate(new_ff);
                }}
              />;
          }
        break;
        // case "Creature Spell as Ability":
        //   if (my_ff instanceof CreatureSpellAsAbility) {
        //     const ability = my_ff as CreatureSpellAsAbility;
        //     feature_details = 
        //       <CreatureSpellAsAbilityInput 
        //         obj={ability.clone()}
        //         disabled={disabled}
        //         onChange={makeUpdate}
        //       />;
        //   }
        // break;
        case "Advantage":
          if (my_ff instanceof Advantage) {
            const advantage = my_ff as Advantage;
            feature_details =
              <AdvantageInput
                obj={advantage.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Greater Advantage":
          if (my_ff instanceof GreaterAdvantage) {
            const advantage = my_ff as GreaterAdvantage;
            feature_details =
              <GreaterAdvantageInput
                obj={advantage.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Damage Multiplier":
          if (my_ff instanceof DamageMultiplier) {
            const resistance = my_ff as DamageMultiplier;
            feature_details = 
              <DamageMultiplierInput
                obj={resistance.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Spellcasting": 
          if (my_ff instanceof SpellcastingFeature) {
            const spellcasting = my_ff as SpellcastingFeature;
            feature_details = 
              <SpellcastingFeatureInput
                obj={spellcasting.clone()}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Spellcasting Focus":
          if (my_ff instanceof StringFeature) {
            const focus = my_ff as StringFeature;
            feature_details = 
              <SelectStringBox 
                name="Spellcasting Focus" 
                options={SPELLCASTING_FOCUSES}
                value={focus.str} 
                disabled={disabled}
                onChange={(value: string) => {
                  const new_ff = my_ff as StringFeature;
                  new_ff.str = value;
                  makeUpdate(new_ff);
                }} 
              />;
          }
        break;
        case "Spellbook":
          if (my_ff instanceof Spellbook) {
            const book = my_ff as Spellbook;
            feature_details = 
              <SpellbookInput
                obj={book.clone()} 
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate} 
              />;
          }
        break;
        case "Spellbook Modifier":
          if (my_ff instanceof SpellbookModifier) {
            const mod = my_ff as SpellbookModifier;
            feature_details = 
              <SpellbookModifierInput
                obj={mod.clone()} 
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate} 
              />;
          }
        break;
        case "Spells":
          if (my_ff instanceof NumberFeature) {
            const spells_prepared = my_ff as NumberFeature;
            feature_details = 
              <StringBox 
                name="Spells"
                value={`${spells_prepared}`} 
                type="number"
                disabled={disabled}
                onBlur={(value: string) => {
                  const new_ff = my_ff as NumberFeature;
                  new_ff.num = +value;
                  makeUpdate(new_ff);
                }} 
              />;
          }
        break;
        case "Cantrips":
          if (my_ff instanceof NumberFeature) {
            const cantrips = my_ff as NumberFeature;
            feature_details = 
              <StringBox 
                name="Cantrips"
                value={`${cantrips.num}`} 
                type="number"
                disabled={disabled}
                onBlur={(value: string) => {
                  const new_ff = my_ff as NumberFeature;
                  new_ff.num = +value;
                  makeUpdate(new_ff);
                }} 
              />;
          }
        break;
        // case "Cantrips from List":
        case "Spells from List":
          if (my_ff instanceof SpellsFromList) {
            const spells_from_list = my_ff as SpellsFromList;
            feature_details = <SpellsFromListInput 
              obj={spells_from_list}
              disabled={disabled}
              attached_table={attached_table}
              onChange={makeUpdate} 
            />;
          }
        break;
        case "Spells from List with Costs":
          if (my_ff instanceof SpellsFromListWithCosts) {
            const spells_from_list = my_ff;
            feature_details = <SpellsFromListWithCostsInput 
              obj={spells_from_list}
              disabled={disabled}
              attached_table={attached_table}
              onChange={makeUpdate} 
            />;
          }
        break;
        case "Extra Spell Lists":
          if (my_ff instanceof ExtraSpellLists) {
            const esl = my_ff as ExtraSpellLists;
            feature_details = <ExtraSpellListsInput 
              obj={esl}
              disabled={disabled}
              onChange={makeUpdate} 
            />;
          }
        break;
        case "Ritual Casting":
        break;
        case "Bonus Spells":
          if (my_ff instanceof BonusSpells) {
            const list = my_ff as BonusSpells;
            feature_details = 
              <BonusSpellsFeatureInput
                obj={list.clone()} 
                disabled={disabled}
                onChange={makeUpdate} 
              />;
          }
        break;
        case "Bonus Spells Upgrade":
          if (my_ff instanceof BonusSpellsUpgrade) {
            const list = my_ff as BonusSpellsUpgrade;
            feature_details = 
              <BonusSpellsUpgradeFeatureInput
                obj={list.clone()} 
                disabled={disabled}
                onChange={makeUpdate} 
              />;
          }
        break;
        case "Suggested Spells":
          if (my_ff instanceof SuggestedSpells) {
            const suggestions = my_ff as SuggestedSpells;
            feature_details = 
              <SuggestionsInput
                obj={suggestions.clone()}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Action Type Upgrade":
          if (my_ff instanceof ActionTypeUpgrade) {
            const atu = my_ff as ActionTypeUpgrade;
            feature_details = 
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 6
                  }}>
                  <SelectObjectBox
                    name="Action Types"
                    values={atu.action_types} 
                    data_type={"action_type"}
                    disabled={disabled}
                    multiple
                    onChange={(changed: string[]) => {
                      const new_ff = my_ff as ActionTypeUpgrade;
                      new_ff.action_types = changed;
                      makeUpdate(new_ff);
                    }} 
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6
                  }}>
                  <SelectStringBox 
                    name="Casting Time"
                    value={ atu.reaction ? "Reaction" : "Bonus Action" }
                    options={["Bonus Action","Reaction"]}
                    disabled={disabled}
                    onChange={(changed: string) => {
                      const new_ff = my_ff as ActionTypeUpgrade;
                      new_ff.reaction = changed === "Reaction";
                      makeUpdate(new_ff);
                    }} 
                  />
                </Grid>
              </Grid>;
          }
        break;
        case "Ignore Stealth Disadvantage":
          if (my_ff instanceof IgnoreStealthDisadvantage) {
            const atu = my_ff as IgnoreStealthDisadvantage;
            feature_details = 
              <Grid>
                <SelectStringBox
                  name="Armor Types"
                  options={ARMOR_TYPE_OPTIONS}
                  values={atu.armor_type_ids} 
                  disabled={disabled}
                  multiple
                  onChange={(changed: string[]) => {
                    const new_ff = my_ff as IgnoreStealthDisadvantage;
                    new_ff.armor_type_ids = changed;
                    makeUpdate(new_ff);
                  }} 
                />
              </Grid>;
          }
        break;
        case "Special Feat Access":
          if (my_ff instanceof StringFeature) {
            const sfa = my_ff as StringFeature;
            feature_details = 
              <Grid>
                <SelectStringBox
                  name="Special Feat Type"
                  value={sfa.str} 
                  options={["Fighting Style"]}
                  disabled={disabled}
                  onChange={(value: string) => {
                    const new_ff = my_ff as StringFeature;
                    new_ff.str = value;
                    makeUpdate(new_ff);
                  }} 
                />
              </Grid>;
          }
        break;
        case "Feat":
          if (my_ff instanceof FeatFeature) {
            feature_details = 
              <FeatFeatureInput 
                obj={my_ff} 
                change_time={change_time}
                disabled={disabled}
                onChange={makeUpdate} 
              />;
          }
        break;
        case "Selectable":
          if (my_ff instanceof Selectable) {
            const ff = my_ff as Selectable;
            feature_details = 
              <SelectableInput 
                obj={ff.clone()}
                change_time={change_time}
                disabled={disabled}
                onChange={makeUpdate}
                attached_table={attached_table}
              />;
          }
        break;
        case "Resource":
          if (my_ff instanceof ResourceFeature) {
            const resource_feature = my_ff as ResourceFeature;
            feature_details = 
              <ResourceFeatureInput
                obj={resource_feature.clone()}
                disabled={disabled}
                attached_table={attached_table}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Ability Score Improvement":
          if (my_ff instanceof ASIBaseFeature) {
            const asi = my_ff as ASIBaseFeature;
            feature_details = 
              <ASIBaseFeatureInput
                obj={asi.clone()}
                disabled={disabled}
                onChange={makeUpdate}
              />;
          }
        break;
        case "Mystic Arcanum":
          if (my_ff instanceof NumberFeature) {
            const level = my_ff as NumberFeature;
            feature_details = 
              <StringBox 
                name="Mystic Arcanum Level" 
                value={`${level.num}`} 
                type="number"
                disabled={disabled}
                onBlur={(value: string) => {
                  const new_ff = my_ff as NumberFeature;
                  new_ff.num = +value;
                  makeUpdate(new_ff);
                }} 
              />;
          }
        break;
        case "Spell Mastery":
          if (my_ff instanceof NumberFeature) {
            const level = my_ff as NumberFeature;
            feature_details = 
              <StringBox 
                name="Spell Mastery Level" 
                value={`${level.num}`} 
                type="number"
                disabled={disabled}
                onBlur={(value: string) => {
                  const new_ff = my_ff as NumberFeature;
                  new_ff.num = +value;
                  makeUpdate(new_ff);
                }} 
              />;
          }
        break;
      }
    }
    return (
      <Grid size={12}>
        { feature_details }
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    // console.log(floating_feature);
    // console.log(my_ff);
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}
          style={{ height: `${height - 40}px`, overflowY: "scroll", overflowX: "hidden" }}
          container spacing={0} direction="row">
          <Grid size={12}>
            <CopyButton label='Copy Feature' 
              onClick={ () => {
                if (api.user) {
                  const new_ff = my_ff.clone(true);
                  new_ff.owner_id = api.user.admin ? "" : api.user._id;
                  new_ff.owner_name = api.user.use_name;
                  setFF(new_ff);
                  if (onAdd2) {
                    onAdd2(new_ff);
                  }
                }
              }} 
            />
            <span className="red padded">
              { my_ff._id === "" ? 
                "New Floating Feature, Unsaved" 
              : unsaved_changes && 
                "Unsaved Changes"
              }
            </span>
          </Grid>
          <Grid size={6}>
            <StringBox 
              name="Feature Name (Should be descriptive and unique)"
              value={my_ff.name} 
              disabled={disabled || !api.online}
              onBlur={(changed: string) => {
                const new_ff = my_ff.clone(true);
                new_ff.name = changed;
                if (new_ff.real_name === "") {
                  new_ff.real_name = changed;
                }
                setFF(new_ff);
                setUnsavedChanges(true);
                if (unsavedChange) {
                  unsavedChange(true);
                }
              }}
            />
          </Grid>
          <Grid size={6}>
            <StringBox 
              name="Real Name (What it's actually named)"
              value={my_ff.real_name} 
              disabled={disabled || !api.online}
              onBlur={(changed: string) => {
                const new_ff = my_ff.clone();
                new_ff.real_name = changed;
                if (new_ff.real_name === "") {
                  new_ff.real_name = new_ff.name;
                }
                setFF(new_ff);
                setUnsavedChanges(true);
                if (unsavedChange) {
                  unsavedChange(true);
                }
              }}
            />
          </Grid>
          <Grid size={12}>
            <DescriptionBox
              value={my_ff.description}
              cfs={cfs}
              disabled={!api.online}
              onChange={(changed: string) => {
                const new_ff = my_ff.clone();
                new_ff.description = changed;
                setFF(new_ff);
                setUnsavedChanges(true);
                if (unsavedChange) {
                  unsavedChange(true);
                }
              }}
            />
          </Grid>
          <Grid size={12}
            container spacing={0} direction="row">
            <Grid size={6}>
              <SelectStringBox 
                name="Feature Type"
                options={allowed_feature_types.sort((a,b) => { return a.localeCompare(b); })}
                value={my_ff.feature_type} 
                disabled={disabled || !api.online}
                onChange={(changed: FeatureType) => {
                  const old_ff = my_ff;
                  let new_ff: FloatingFeature | null = null;
                  switch(changed) {
                    case "Language":
                      new_ff = new LanguageFeature();
                    break;
                    case "Pact Boon":
                      new_ff = new PactBoonFeature();
                    break;
                    case "Proficiency Override":
                      new_ff = new StringFeature();
                      (new_ff as StringFeature).str = "Proficiency Override";
                    break;
                    case "Ignore Heavy Rule":
                      new_ff = new StringFeature();
                      (new_ff as StringFeature).str = "Ignore Heavy Rule";
                    break;
                    case "Eldritch Invocations":
                      new_ff = new EldritchInvocationsFeature();
                    break;
                    case "Creature Type":
                      new_ff = new CreatureTypeFeature();
                    break;
                    case "Fighting Style":
                      new_ff = new StringsFeature();
                      (new_ff as StringsFeature).strings = [];
                    break;
                    case "Reroll":
                      new_ff = new Reroll();
                    break;
                    case "Modifier":
                      new_ff = new Modifier();
                    break;
                    case "Minimum Roll":
                      new_ff = new MinimumRoll();
                    break;
                    case "Learnable":
                      new_ff = new LearnableFeature();
                    break;
                    case "Unarmed Strikes":
                      new_ff = new UnarmedStrikesFeature();
                    break;
                    case "Replace Ability Score for Attacks":
                      new_ff = new ReplaceAbilityScoreFeature();
                    break;
                    case "Attack Upgrade":
                      new_ff = new AttackUpgradeFeature();
                    break;
                    case "Weapon Mastery":
                      new_ff = new WeaponMasteryFeature();
                    break;
                    case "Weapon Mastery Master":
                      new_ff = new WeaponMasteryMasterFeature();
                    break;
                    case "Extra Effects":
                      new_ff = new ExtraEffectsFeature();
                    break;
                    case "Disable Actions":
                      new_ff = new DisableActionsFeature();
                    break;
                    case "Extra Effect Type":
                      new_ff = new ExtraEffectTypeFeature();
                      if (old_ff instanceof ExtraEffectsFeature) {
                        (new_ff as ExtraEffectTypeFeature).copyExtraEffectsFeature(old_ff);
                      }
                    break;
                    case "Extra Effects of Type":
                      new_ff = new ExtraEffectsOfTypeFeature();
                      if (old_ff instanceof ExtraEffectsFeature) {
                        (new_ff as ExtraEffectsOfTypeFeature).copyExtraEffectsFeature(old_ff);
                      }
                    break;
                    case "Bonus Action Attack":
                      new_ff = new BonusActionAttackFeature();
                    break;
                    case "Light Attack Add Damage Mod":
                      new_ff = new LightAttackAddDamageMod();
                    break;
                    case "Weapon Keyword Upgrade":
                      new_ff = new WeaponKeywordUpgradeFeature();
                    break;
                    case "Weapon Keyword Upgrade 2":
                      new_ff = new WeaponKeywordUpgradeFeature2();
                    break;
                    case "Weapon Damage Upgrade":
                      new_ff = new WeaponDamageUpgradeFeature();
                    break;
                    case "Resource Refresh Rule Change":
                      const sf = new StringsFeature();
                      sf.strings = ["","Short Rest"];
                      new_ff = sf;
                    break;
                    case "Subclass":
                      new_ff = new SubclassFeature();
                    break;
                    case "Subrace":
                      new_ff = new StringFeature();
                    break;
                    case "Start Equipment":
                      new_ff = new StartEquipmentFeature();
                    break;
                    case "Characteristic":
                      new_ff = new CharacteristicFeature();
                    break;
                    case "Spell Modifier":
                      new_ff = new SpellModifier();
                    break;
                    case "Spell Damage Options":
                      new_ff = new SpellDamageOptions();
                    break;
                    case "Size Options":
                      new_ff = new SizeOptions();
                    break;
                    case "Summon Upgrade":
                      new_ff = new SummonUpgradeFeature();
                    break;
                    case "Condition":
                      new_ff = new ConditionFeature();
                    break;
                    case "Alternate Condition":
                      new_ff = new AlternateConditionFeature();
                    break;
                    case "Allowed Conditions":
                      new_ff = new StringsFeature();
                    break;
                    case "Limit Spoof":
                      new_ff = new LimitSpoofFeature();
                    break;
                    case "Rest Alteration":
                      new_ff = new RestAlterationFeature();
                    break;
                    case "Specific Special Feature":
                    case "Special Feature":
                    case "Special Feature Choices":
                      new_ff = new SFFeature();
                    break;
                    case "Special Feature List Merge":
                      new_ff = new SFMergeFeature();
                    break;
                    case "Sense":
                      new_ff = new SenseFeature();
                    break;
                    case "Proficiencies":
                    case "Expertise":
                      new_ff = new Proficiency();
                    break;
                    case "Jack of All Trades":
                      new_ff = new NumberFeature();
                      (new_ff as NumberFeature).num = 1;
                    break;
                    case "Feat":
                      new_ff = new FeatFeature();
                      // (new_ff as FeatFeature).max_level = 1;
                    break;
                    case "Selectable":
                      new_ff = new Selectable();
                    break;
                    case "Ability":
                      new_ff = new Ability();
                    break;
                    case "Creature Ability":
                      new_ff = new CreatureAbility();
                    break;
                    case "Skill as Ability":
                      new_ff = new SkillAsAbility();
                    break;
                    case "Skill Ability Override":
                      new_ff = new SkillAbilityOverrideFeature();
                    break;
                    case "Extra Attacks":
                      new_ff = new NumberFeature();
                      (new_ff as NumberFeature).num = 1;
                    break;
                    case "Minion Extra Attacks":
                      new_ff = new UpgradableNumberFeature();
                    break;
                    case "Minion Ability":
                      new_ff = new MinionAbility();
                    break;
                    // case "Creature Spell as Ability":
                    //   new_ff = new CreatureSpellAsAbility();
                    // break;
                    case "Advantage":
                      new_ff = new Advantage();
                    break;
                    case "Greater Advantage":
                      new_ff = new GreaterAdvantage();
                    break;
                    case "Damage Multiplier":
                      new_ff = new DamageMultiplier();
                    break;
                    case "Spellcasting":
                      new_ff = new SpellcastingFeature();
                    break;
                    case "Spellcasting Focus":
                      new_ff = new StringFeature();
                      (new_ff as StringFeature).str = "Arcane Focus";
                    break;
                    case "Bonus Spells":
                      new_ff = new BonusSpells();
                    break;
                    case "Bonus Spells Upgrade":
                      new_ff = new BonusSpellsUpgrade();
                    break;
                    case "Action Type Upgrade": 
                      new_ff = new ActionTypeUpgrade();
                    break;
                    case "Ignore Stealth Disadvantage":
                      new_ff = new IgnoreStealthDisadvantage();
                    break;
                    case "Suggested Spells":
                      new_ff = new SuggestedSpells();
                    break;
                    case "Special Feat Access":
                      new_ff = new StringFeature();
                      (new_ff as StringFeature).str = "Fighting Style";
                    break;
                    case "Spellbook":
                      new_ff = new Spellbook();
                    break;
                    case "Spellbook Modifier":
                      new_ff = new SpellbookModifier();
                    break;
                    case "Spells":
                      new_ff = new NumberFeature();
                    break;
                    case "Cantrips":
                      new_ff = new NumberFeature();
                    break;
                    // case "Cantrips from List":
                    case "Spells from List":
                      new_ff = new SpellsFromList();
                    break;
                    case "Spells from List with Costs":
                      new_ff = new SpellsFromListWithCosts();
                    break;
                    case "Extra Spell Lists":
                      new_ff = new ExtraSpellLists();
                    break;
                    case "Ritual Casting":
                      new_ff = new StringFeature();
                      (new_ff as StringFeature).str = "Ritual Casting";
                    break;
                    case "Resource":
                      new_ff = new ResourceFeature();
                    break;
                    case "Ability Score Improvement":
                      new_ff = new ASIBaseFeature();
                    break;
                    case "Mystic Arcanum":
                      new_ff = new NumberFeature();
                      (new_ff as NumberFeature).num = 1;
                    break;
                    case "Spell Mastery":
                      new_ff = new NumberFeature();
                      (new_ff as NumberFeature).num = 1;
                    break;
                    case "Transform Upgrade":
                      new_ff = new TransformUpgradeFeature();
                    break;
                    case "Knowledge":
                      new_ff = new KnowledgeFeature();
                    break;
                  }
                  if (!new_ff) {
                    new_ff = new FloatingFeature();
                  }
                  if (new_ff) {
                    new_ff.copy(old_ff);
                    new_ff.feature_type = changed;
                  }
                  setFF(new_ff);
                  setUnsavedChanges(true);
                  if (unsavedChange) {
                    unsavedChange(true);
                  }
                }}
              />
            </Grid>
          </Grid>
          { renderDetails() }
        </Grid>
        { !disabled && api.online &&
          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              disabled={ my_ff.name === "" || processing }
              onClick={submit}>
              Submit
            </Button>
            { floating_feature &&
              <Button
                variant="contained"
                disabled={ processing }
                style={{ marginLeft: "4px" }}
                onClick={ () => { 
                  const new_ff = get_floating_feature(floating_feature);
                  setFF(new_ff);
                  setUnsavedChanges(false);
                  if (unsavedChange) {
                    unsavedChange(false);
                  }
                }}>
                Cancel
              </Button>
            }
          </Grid>
        }
      </Grid>
    );
  }
}

